import siteStats from '@data/site-stats';

/**
 * Namespaces whose copy names payment assets or networks. A page's messages
 * are serialised into its HTML (`__NEXT_DATA__`), so these are left out of
 * every page that does not ask for them by name.
 */
export const SENSITIVE_NAMESPACES = [
  'stablecoins',
  'checkout',
  'network',
  'networksIndex',
  'currencies',
  'wallet',
];

const cache = new Map();

/**
 * Messages for a page's `getStaticProps` / `getServerSideProps`.
 *
 *   messages: loadMessages(locale)
 *   messages: loadMessages(locale, { include: ['network'] })
 *
 * Loads `src/locales/<locale>.json`, drops the sensitive namespaces the page
 * did not `include`, and fills in the marketing numbers (`{brandCount}`,
 * `{countryCount}`, `{networkCount}`) from `data/site-stats.js`, in the number
 * format of `locale`.
 *
 * The result is cached and shared between calls: treat it as read-only.
 */
export function loadMessages(locale, { include = [] } = {}) {
  const unknown = include.filter((namespace) => !SENSITIVE_NAMESPACES.includes(namespace));
  if (unknown.length > 0) {
    throw new Error(
      `loadMessages: "${unknown.join('", "')}" is not a sensitive namespace. ` +
        `Only ${SENSITIVE_NAMESPACES.join(', ')} need to be included; everything else always loads.`
    );
  }

  const cacheKey = `${locale}|${[...include].sort().join(',')}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const all = require(`@/locales/${locale}.json`);
  const kept = {};
  for (const [namespace, value] of Object.entries(all)) {
    if (SENSITIVE_NAMESPACES.includes(namespace) && !include.includes(namespace)) continue;
    kept[namespace] = value;
  }

  const messages = siteStats.applyMarketingStatsToMessages(kept, locale);
  cache.set(cacheKey, messages);
  return messages;
}
